import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHeart as faHeartSolid,
    faDownload,
    faEdit,
    faBookmark as faBookmarkSolid,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import {
    faHeart as faHeartOutline,
    faBookmark as faBookmarkOutline,
} from '@fortawesome/free-regular-svg-icons';
import { useFavorites } from '../../context/FavoritesContext';
import { useCollections } from '../../context/CollectionsContext';
import { useAuth } from '../../context/AuthContext';
import { useDownload } from '../../context/DownloadContext';

// A single round, glassy icon button used in the hover cluster.
const IconButton = ({ icon, label, onClick, active, activeClass = '', spin = false }) => (
    <button
        type="button"
        onClick={onClick}
        aria-label={label}
        title={label}
        className={`tap-target h-9 w-9 flex items-center justify-center rounded-full bg-white/85 dark:bg-gray-900/80 backdrop-blur shadow-e1 ring-1 ring-black/5 hover:scale-110 hover:bg-white dark:hover:bg-gray-900 transition-all duration-150 ${
            active ? activeClass : 'text-gray-600 dark:text-gray-300'
        }`}
    >
        <FontAwesomeIcon icon={icon} spin={spin} className="text-[15px]" />
    </button>
);

/**
 * Hover-revealed action cluster for image / video cards.
 * - favorite  (heart)     — save to favorites
 * - collection (bookmark)  — save to a collection
 * - download  (arrow)     — save the asset locally
 * - edit      (pencil)    — open the TUI image editor (images only; pass `editUrl`)
 */
const CardActions = ({ kind, item, downloadUrl, downloadFilename, editUrl }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isFavorite, toggleFavorite } = useFavorites();
    const { isInCollection, toggleCollection } = useCollections();
    const { download, downloading } = useDownload();

    const fav = isFavorite(kind, item.id);
    const saved = isInCollection(kind, item.id);
    const isDownloading = downloading === downloadUrl;

    const stop = (fn) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        fn();
    };

    // Editing requires an account. Send unauthenticated users to log in,
    // returning them straight to the editor once they're signed in.
    const editHref = `/edit?src=${encodeURIComponent(editUrl || '')}&name=${encodeURIComponent(downloadFilename || 'pixiy-edit.png')}`;
    const openEditor = () => {
        if (!user) {
            navigate('/login', { state: { from: { pathname: '/edit', search: editHref.slice(editHref.indexOf('?')) } } });
            return;
        }
        navigate(editHref);
    };

    return (
        <div
            className="absolute top-3 right-3 z-10 flex flex-col items-center gap-2
                       opacity-100 translate-y-0
                       md:opacity-0 md:-translate-y-1 md:group-hover:opacity-100 md:group-hover:translate-y-0
                       transition-all duration-300"
        >
            <IconButton
                icon={fav ? faHeartSolid : faHeartOutline}
                label={fav ? 'Remove from favorites' : 'Save to favorites'}
                onClick={stop(() => toggleFavorite(kind, item))}
                active={fav}
                activeClass="text-rose-500"
            />
            <IconButton
                icon={saved ? faBookmarkSolid : faBookmarkOutline}
                label={saved ? 'Remove from collection' : 'Add to collection'}
                onClick={stop(() => toggleCollection(kind, item))}
                active={saved}
                activeClass="text-brand-500"
            />
            <IconButton
                icon={isDownloading ? faSpinner : faDownload}
                label={isDownloading ? 'Downloading…' : 'Download'}
                onClick={stop(() => download(downloadUrl, downloadFilename))}
                spin={isDownloading}
            />
            {editUrl && (
                <IconButton
                    icon={faEdit}
                    label="Edit image"
                    onClick={stop(openEditor)}
                />
            )}
        </div>
    );
};

export default CardActions;
